import { z } from 'zod';
import * as permissions from '../permissions';
import { MarketCountryCode } from '@axo/shared/types';

export const CountrySchema = z.nativeEnum(MarketCountryCode);

export const ActionSchema = z.object({
  Role: permissions.RoleSchema,
  Kind: z.string(),
  Name: z.string(),
  Description: z.string(),
});
export type Action = z.infer<typeof ActionSchema>;

export const AssigneeSchema = z.object({
  ID: z
    .string()
    .describe("The user's ClientID from the authentication service"),
  AvailableInCountry: CountrySchema,
  CreatedAt: z.string().datetime(),
  UpdatedAt: z.string().datetime(),
});
export type Assignee = z.infer<typeof AssigneeSchema>;

export const ApplicationBucketSchema = z.object({
  LoanApplicationID: z.string(),
  BucketID: z.string(),
  AssigneeID: z.string(),
  ProcessedAt: z.string().datetime().nullable(),
  ProcessedBy: z.string(),
  Country: CountrySchema,
  CreatedAt: z.string().datetime(),
  UpdatedAt: z.string().datetime(),
  BucketUpdatedAt: z.string().datetime(),
  Revision: z.number().int(),
});
export type ApplicationBucket = z.infer<typeof ApplicationBucketSchema>;

export const ApplicationBucketSummarySchema = z.object({
  BucketID: z.string(),
  NumApplicationsAssigned: z.number().int(),
  NumApplicationsUnassigned: z.number().int(),
  Total: z.number().int(),
});
export type ApplicationBucketSummary = z.infer<
  typeof ApplicationBucketSummarySchema
>;

export const BucketSchema = z.object({
  ID: z.string(),
  Name: z.string(),
  Priority: z.number().int(),
  RequiresACEAction: z.boolean(),
  Actions: z.array(ActionSchema).nullable(),
  ConditionsExplanation: z.string(),
  CreatedAt: z.string().datetime(),
  UpdatedAt: z.string().datetime(),
});
export type Bucket = z.infer<typeof BucketSchema>;

export const FindApplicationBucketReqSchema = z.object({
  AssigneeID: z.string().optional(),
  Processed: z.boolean().optional(),
  BucketID: z.string().optional(),
});
export type FindApplicationBucketReq = z.infer<
  typeof FindApplicationBucketReqSchema
>;

export const FindApplicationBucketResSchema = z.object({
  Items: z.array(ApplicationBucketSchema).nullable(),
});
export type FindApplicationBucketRes = z.infer<
  typeof FindApplicationBucketResSchema
>;

export const ListBucketsReqSchema = z.object({
  country: z.string().optional(),
  offset: z.number().optional(),
  limit: z.number().optional(),
});
export type ListBucketsReq = z.infer<typeof ListBucketsReqSchema>;

export const ListBucketsResSchema = z.object({
  Items: z.array(BucketSchema).nullable(),
  Offset: z.number().int(),
});
export type ListBucketsRes = z.infer<typeof ListBucketsResSchema>;

export const ListApplicationBucketRevisionsResSchema = z.object({
  Items: z.array(ApplicationBucketSchema).nullable(),
});
export type ListApplicationBucketRevisionsRes = z.infer<
  typeof ListApplicationBucketRevisionsResSchema
>;

export const GetApplicationBucketSummariesResSchema = z.object({
  Items: z.array(ApplicationBucketSummarySchema).nullable(),
});
export type GetApplicationBucketSummariesRes = z.infer<
  typeof GetApplicationBucketSummariesResSchema
>;

export const ProcessApplicationBucketResSchema = ApplicationBucketSchema;
export type ProcessApplicationBucketRes = z.infer<
  typeof ProcessApplicationBucketResSchema
>;

export const GetApplicationBucketResSchema = ApplicationBucketSchema;
export type GetApplicationBucketRes = z.infer<
  typeof GetApplicationBucketResSchema
>;

export const GetBucketResSchema = BucketSchema;
export type GetBucketRes = z.infer<typeof GetBucketResSchema>;
